// Google Ads constants for the CLW / Launch-details review grid.
//
// Covers the three Google Ads channels that reach planning review as their own
// destination: Search (`g-ads-search`), Performance Max (`g-ads-pmax`) and
// Demand Gen (`g-ads-demandgen`). Pure data — no React, no I/O — mirroring the
// shape of ./groundtruth/gtConstants.js so the review surfaces can pick a
// column set / editable set / editor map by subtype without JSX branching.
//
// The three channels share one campaign envelope but differ in what a grid ROW
// represents, so each gets its own column set:
//   search     → one row per ad group      (campaigns[].ad_groups[])
//   pmax       → one row per asset group   (campaigns[].assets[] → assets[])
//   demandgen  → one row per asset group   (campaigns[].assets[] → assets[])

import { editableFieldsFor } from '../clwGridConstants';
// The share × units arithmetic applyGadsCampaignFields writes through — reused
// by GADS_SCALED_FIELDS below so the preview can't disagree with the write.
import { scaleToParent } from '../sharedFieldWriters';

// ── Subtype resolution ───────────────────────────────────────────────
// Every spelling these ids arrive in. `launcherResult.platform` uses the
// import convention ('g-ads-search'); `campaigns.destination` uses the
// create-form channel id ('google-ads-search'); older payloads and the
// launcher's PLATFORM_MAPPING shorthand use bare 'search' / 'pmax' /
// 'demandgen'. Mirrors campaign-services/handlers/campaign_launcher_handler.py
// (PLATFORM_MAPPING) and utils/accountSettingPlatformList.js
// (PLATFORM_ALIASES) — keep the three in sync when a spelling is added.
const SUBTYPE_BY_TOKEN = {
  search: 'search',
  pmax: 'pmax',
  demandgen: 'demandgen',
  'demand-gen': 'demandgen',
  'demand_gen': 'demandgen',
  performancemax: 'pmax',
  'performance-max': 'pmax',
  'performance_max': 'pmax',
};

/**
 * Resolve any Google Ads platform / destination spelling to its subtype.
 * Returns 'search' | 'pmax' | 'demandgen', or null for everything else
 * (including google-ads-display, which has no review surface yet).
 *
 * Matching is token-based rather than substring-based so 'g-ads-display'
 * doesn't accidentally resolve, and a non-Google platform that happens to
 * contain the word 'search' can never be mistaken for a Google Ads plan.
 */
export function gadsSubtype(platform) {
  const s = String(platform || '').trim().toLowerCase();
  if (!s) return null;
  // Must look like a Google Ads id: 'g-ads-*', 'google-ads-*', 'google_*',
  // 'gads-*', or a bare channel shorthand.
  const bare = SUBTYPE_BY_TOKEN[s];
  if (bare) return bare;
  const isGoogle = /^(g[-_]?ads|google([-_]?ads)?)[-_]/.test(s);
  if (!isGoogle) return null;
  const tail = s.replace(/^(g[-_]?ads|google([-_]?ads)?)[-_]/, '');
  return SUBTYPE_BY_TOKEN[tail] || null;
}

/** True when `platform` names one of the three supported Google Ads channels. */
export const isGadsPlatform = (platform) => gadsSubtype(platform) != null;

// Display labels for the header / summary strip / grid footer.
export const GADS_SUBTYPE_LABELS = {
  search: 'Google Ads Search',
  pmax: 'Google Ads Performance Max',
  demandgen: 'Google Ads Demand Gen',
};

// Short format-pill code per subtype — the `fmt` field on every row. Drives
// the format breakdown table and the pill colour class.
export const GADS_FMT = {
  search: 'SEM',
  pmax: 'PMAX',
  demandgen: 'DGEN',
};

// What one row represents, for the grid footer / QA table headers.
export const GADS_ROW_LABEL = {
  search: 'ad groups',
  pmax: 'asset groups',
  demandgen: 'asset groups',
};

// ── Money ────────────────────────────────────────────────────────────
// Google Ads serialises every monetary value in micros (1e6 = one unit of
// the account currency).
export const microsToUnits = (micros) => {
  const n = Number(micros);
  return Number.isFinite(n) ? n / 1e6 : null;
};

// ── Column groups ────────────────────────────────────────────────────
// Same seven tabs as DV360 / TTD / GT so the grid's tab bar is identical
// across platforms; the columns inside are Google-Ads-native. Keys map to
// fields written by buildGadsReviewRows().

const SEARCH_GROUP_COLS = {
  overview: [
    { k: 'fmt', l: 'Type', w: 76 },
    { k: 'market', l: 'Campaign', w: 220 },
    { k: 'matchType', l: 'Match type', w: 110 },
    { k: 'keywords', l: 'Keywords', w: 120 },
    { k: 'start', l: 'Start', w: 96, fmt: 'date' },
    { k: 'end', l: 'End', w: 96, fmt: 'date' },
    { k: 'budget', l: 'Budget', w: 110, fmt: 'cur' },
  ],
  flighting: [
    { k: 'start', l: 'Start', w: 110, fmt: 'date' },
    { k: 'end', l: 'End', w: 110, fmt: 'date' },
    { k: 'flightType', l: 'Flight type', w: 170 },
    { k: 'dailyBudget', l: 'Daily budget', w: 120, fmt: 'cur' },
    { k: 'pacing', l: 'Budget type', w: 140 },
    { k: 'entityStatus', l: 'Status', w: 100 },
  ],
  budget: [
    { k: 'market', l: 'Campaign', w: 220 },
    { k: 'dailyBudget', l: 'Daily budget', w: 120, fmt: 'cur' },
    { k: 'budget', l: 'Total budget', w: 120, fmt: 'cur' },
    { k: 'bidStrategy', l: 'Bid strategy', w: 180 },
    { k: 'pacing', l: 'Budget type', w: 140 },
  ],
  targeting: [
    { k: 'keywords', l: 'Keywords', w: 130 },
    { k: 'negativeKeywords', l: 'Negatives', w: 120 },
    { k: 'geo', l: 'Locations', w: 220 },
    { k: 'zipcodes', l: 'Zip codes', w: 130 },
    { k: 'matchType', l: 'Match type', w: 110 },
    { k: 'languages', l: 'Languages', w: 130 },
  ],
  creative: [
    { k: 'headlines', l: 'Headlines', w: 120 },
    { k: 'descriptions', l: 'Descriptions', w: 130 },
    { k: 'clickURL', l: 'Final URL', w: 260 },
    { k: 'displayPath', l: 'Display path', w: 160 },
    { k: 'sitelinks', l: 'Sitelinks', w: 110 },
  ],
  bid: [
    { k: 'bidStrategy', l: 'Strategy', w: 200 },
    { k: 'targetLabel', l: 'Target', w: 150 },
    { k: 'matchType', l: 'Match type', w: 110 },
  ],
  tracking: [
    { k: 'clickURL', l: 'Final URL', w: 300 },
    { k: 'displayPath', l: 'Display path', w: 180 },
    { k: 'market', l: 'Campaign', w: 220 },
    { k: 'countryCode', l: 'Country', w: 100 },
  ],
};

// PMax and Demand Gen share the asset-group row shape; the differences are
// PMax's search themes / long headline versus Demand Gen's audience
// targeting and demographic filters.
const PMAX_GROUP_COLS = {
  overview: [
    { k: 'fmt', l: 'Type', w: 80 },
    { k: 'market', l: 'Campaign', w: 220 },
    { k: 'assetKey', l: 'Asset group', w: 150 },
    { k: 'start', l: 'Start', w: 96, fmt: 'date' },
    { k: 'end', l: 'End', w: 96, fmt: 'date' },
    { k: 'targetLabel', l: 'Target', w: 150 },
    { k: 'budget', l: 'Budget', w: 110, fmt: 'cur' },
  ],
  flighting: [
    { k: 'start', l: 'Start', w: 110, fmt: 'date' },
    { k: 'end', l: 'End', w: 110, fmt: 'date' },
    { k: 'flightType', l: 'Flight type', w: 170 },
    { k: 'dailyBudget', l: 'Daily budget', w: 120, fmt: 'cur' },
    { k: 'pacing', l: 'Budget type', w: 140 },
    { k: 'entityStatus', l: 'Status', w: 100 },
  ],
  budget: [
    { k: 'market', l: 'Campaign', w: 220 },
    { k: 'dailyBudget', l: 'Daily budget', w: 120, fmt: 'cur' },
    { k: 'budget', l: 'Total budget', w: 120, fmt: 'cur' },
    { k: 'bidStrategy', l: 'Bid strategy', w: 180 },
    { k: 'targetLabel', l: 'Target', w: 150 },
    { k: 'pacing', l: 'Budget type', w: 140 },
  ],
  targeting: [
    { k: 'geo', l: 'Locations', w: 240 },
    { k: 'keywords', l: 'Search themes', w: 140 },
    { k: 'audiences', l: 'Audiences', w: 160 },
    { k: 'age', l: 'Age', w: 130 },
    { k: 'gender', l: 'Gender', w: 120 },
    { k: 'languages', l: 'Languages', w: 130 },
  ],
  creative: [
    { k: 'headlines', l: 'Headlines', w: 120 },
    { k: 'longHeadline', l: 'Long headline', w: 260 },
    { k: 'descriptions', l: 'Descriptions', w: 130 },
    { k: 'creative', l: 'Images', w: 110 },
    { k: 'videos', l: 'Videos', w: 100 },
    { k: 'businessName', l: 'Business name', w: 180 },
    { k: 'clickURL', l: 'Final URL', w: 260 },
  ],
  bid: [
    { k: 'bidStrategy', l: 'Strategy', w: 200 },
    { k: 'targetLabel', l: 'Target', w: 150 },
    { k: 'pacing', l: 'Budget type', w: 140 },
  ],
  tracking: [
    { k: 'clickURL', l: 'Final URL', w: 300 },
    { k: 'businessName', l: 'Business name', w: 200 },
    { k: 'market', l: 'Campaign', w: 220 },
    { k: 'assetKey', l: 'Asset group', w: 150 },
  ],
};

const DEMANDGEN_GROUP_COLS = {
  ...PMAX_GROUP_COLS,
  targeting: [
    { k: 'geo', l: 'Locations', w: 240 },
    { k: 'audiences', l: 'Audience segments', w: 180 },
    { k: 'interests', l: 'Interests', w: 160 },
    { k: 'age', l: 'Age', w: 150 },
    { k: 'gender', l: 'Gender', w: 130 },
    { k: 'parental', l: 'Parental status', w: 150 },
    { k: 'income', l: 'Income', w: 130 },
  ],
  creative: [
    { k: 'headlines', l: 'Headlines', w: 120 },
    { k: 'descriptions', l: 'Descriptions', w: 130 },
    { k: 'creative', l: 'Images', w: 110 },
    { k: 'videos', l: 'Videos', w: 100 },
    { k: 'businessName', l: 'Business name', w: 180 },
    { k: 'callToAction', l: 'Call to action', w: 160 },
    { k: 'clickURL', l: 'Final URL', w: 260 },
  ],
};

export const GADS_GROUP_COLS_BY_SUBTYPE = {
  search: SEARCH_GROUP_COLS,
  pmax: PMAX_GROUP_COLS,
  demandgen: DEMANDGEN_GROUP_COLS,
};

/** Column set for a subtype; falls back to Search for an unknown value. */
export const gadsGroupCols = (subtype) => GADS_GROUP_COLS_BY_SUBTYPE[subtype] || SEARCH_GROUP_COLS;
export const gadsGroupKeys = (subtype) => Object.keys(gadsGroupCols(subtype));

// ── Editable sets ────────────────────────────────────────────────────
// EDITABLE BY DEFAULT, exactly like DV360 / TTD (see clwGridConstants.js):
// every column of a subtype's grid is editable unless its denylist names it,
// and gads/gadsEdits.js must carry a writer for each — the round-trip tests in
// gadsEdits.test.js enforce that field by field.
//
// Google Ads has no ad-group budget, flight or bid strategy: those live on the
// CAMPAIGN. Those cells are editable anyway, and the write is campaign-scoped
// by construction — every sibling row of the same campaign re-reads the new
// value. (The `budget` cell shows the campaign total's per-unit share, so its
// writer scales share × unit count back up.) That is the Google Ads entity
// model, not a leak: refusing the edit would leave the grid's flight and
// budget columns permanently read-only.
//
// `entityStatus` is the exception, and is denylisted on every subtype: see the
// long note on DV360_DENYLIST in ../clwGridConstants.js. Activation is the
// launch action's job, not a cell — a plan-review surface must not be able to
// promote an entity into a live state. (The Google Ads creators do hardcode
// campaign.status = PAUSED today, so this cell was not the live-launch hole TTD
// had; it is closed for the same reason, and so the three platforms state one
// rule instead of three.)
// Reason maps double as the denylists — keys gate the grid, values are shown
// to the user in the cell detail panel (see clwGridConstants.js).
const GADS_SHARED_READ_ONLY_REASONS = {
  entityStatus: "Activation is the launch action's job — a review grid must never promote a campaign into a live state.",
  fmt: 'A row type tag derived from the subtype — no storage of its own.',
  flightType: 'Google Ads units have no flight of their own — edit the Start/End cells.',
  channel: "A sticky 'Google Ads' label — not a stored value.",
};
const GADS_ASSET_READ_ONLY_REASONS = {
  ...GADS_SHARED_READ_ONLY_REASONS,
  assetKey: 'The identifier that links this asset group to its campaign — renaming it would disconnect the asset group.',
  creativeFormat: 'A derived count summary — edit the headline/description lists instead.',
  name: 'A derived composite of campaign · asset key — the campaign name is the Market column.',
};
export const GADS_READ_ONLY_REASONS_BY_SUBTYPE = {
  search: GADS_SHARED_READ_ONLY_REASONS,
  pmax: GADS_ASSET_READ_ONLY_REASONS,
  demandgen: GADS_ASSET_READ_ONLY_REASONS,
};
export const gadsReadOnlyReasons = (subtype) => GADS_READ_ONLY_REASONS_BY_SUBTYPE[subtype]
  || GADS_SHARED_READ_ONLY_REASONS;
export const GADS_DENYLIST_BY_SUBTYPE = {
  search: new Set(Object.keys(GADS_READ_ONLY_REASONS_BY_SUBTYPE.search)),
  pmax: new Set(Object.keys(GADS_READ_ONLY_REASONS_BY_SUBTYPE.pmax)),
  demandgen: new Set(Object.keys(GADS_READ_ONLY_REASONS_BY_SUBTYPE.demandgen)),
};

// Editable WITHOUT a visible column. Max CPC's column was removed (it renders
// as a meaningless ₹0.01 floor under automated bidding), but the FIELD stays
// writable: the bulk-edit agent resolves "max cpc" onto bidFloor and its
// staged edits flow through applyGadsEdits — dropping the field from this set
// would silently discard those writes.
export const GADS_HIDDEN_EDITABLE = ['bidFloor'];

export const GADS_EDITABLE_BY_SUBTYPE = {
  search: new Set([...editableFieldsFor(SEARCH_GROUP_COLS, GADS_DENYLIST_BY_SUBTYPE.search), ...GADS_HIDDEN_EDITABLE]),
  pmax: editableFieldsFor(PMAX_GROUP_COLS, GADS_DENYLIST_BY_SUBTYPE.pmax),
  demandgen: editableFieldsFor(DEMANDGEN_GROUP_COLS, GADS_DENYLIST_BY_SUBTYPE.demandgen),
};

export const gadsEditableFields = (subtype) => GADS_EDITABLE_BY_SUBTYPE[subtype] || new Set();

// ── Parent-scaled cells (pre-commit disclosure) ──────────────────────
// The Google Ads equivalent of TTD_SCALED_FIELDS / DV360_SCALED_FIELDS in
// ../clwGridConstants.js — see the long note there. Only `budget` qualifies:
// buildGadsReviewRows divides the campaign total by its ad-group / asset-group
// count, so committing the cell writes share × units (and converts to micros).
// `dailyBudget` is campaign-scoped too but is NOT divided — it IS the
// campaign's daily cap — so it has no hidden total to disclose.
//
// `total` goes through the same scaleToParent the applier uses; the micros
// conversion is storage detail, so the preview states the dollar total the user
// is actually setting.
export const GADS_SCALED_FIELDS = {
  budget: {
    scope: 'campaign total budget',
    unit: 'currency',
    // Search rows are ad groups; PMax / Demand Gen rows are asset groups.
    sibNoun: (row) => (row?.fmt === GADS_FMT.search ? 'ad group' : 'asset group'),
    total: (share, row) => scaleToParent(share, row?.siblingCount),
  },
};

// ── List-shaped cells ────────────────────────────────────────────────
// Same shape as TTD_LIST_FIELDS / DV360_LIST_FIELDS: per field, which of
// add / edit / remove the cell detail panel offers, plus the add input's
// placeholder. The geo cell stages ops against the two sub-fields the panel
// uses everywhere — geoInclude / geoExclude — so those are the keys here.
//
// Everything listed is writable by gads/gadsEdits.js#applyGadsEdits against
// the key buildGadsReviewRows renders, which is what makes add and edit safe:
// Google Ads plans state locations, keywords, audiences and copy as plain
// text (no pre-launch ids to resolve), so a typed value is as launchable as a
// parsed one.
const GADS_SEARCH_LIST_FIELDS = {
  keywords: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add keyword…', note: 'New keywords inherit the ad group’s dominant match type' },
  negativeKeywords: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add negative keyword…' },
  headlines: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add headline…' },
  descriptions: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add description…' },
  sitelinks: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add sitelink text…', note: 'Sitelink URLs stay as built — this edits the link text' },
  zipcodes: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add zip code…' },
  geoInclude: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add location or geo id…', note: 'A number (or “Geo 2840”) targets a geo-target constant; anything else is resolved by name at launch' },
  geoExclude: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add location to exclude…' },
};
const GADS_ASSET_LIST_FIELDS = {
  headlines: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add headline…' },
  descriptions: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add description…' },
  audiences: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add audience name…', note: 'Added audiences are resolved by name at launch' },
  // `creative` is the shared grid vocabulary for the images cell. Adds append
  // to the asset's `images` list — the one image key that is a plain list of
  // URLs; removes/edits reach the named slots (logo / marketing / square) too.
  creative: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add image URL…' },
  videos: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add video URL or id…' },
  geoInclude: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add location or geo id…', note: 'A number (or “Geo 2840”) targets a geo-target constant; anything else is resolved by name at launch' },
  geoExclude: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add location to exclude…' },
};

export const GADS_LIST_FIELDS_BY_SUBTYPE = {
  search: GADS_SEARCH_LIST_FIELDS,
  pmax: {
    ...GADS_ASSET_LIST_FIELDS,
    // PMax search themes ride the `keywords` cell (see buildGadsReviewRows).
    keywords: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add search theme…' },
  },
  demandgen: {
    ...GADS_ASSET_LIST_FIELDS,
    interests: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add interest…' },
  },
};

/** List-field capabilities for a subtype; {} for an unknown value. */
export const gadsListFields = (subtype) => GADS_LIST_FIELDS_BY_SUBTYPE[subtype] || {};

// Editor type per column — 'currency' strips $ / commas on commit, 'date'
// uses a native date input, anything unlisted edits as free text.
export const GADS_FIELD_EDITORS = {
  bidFloor: 'currency',
  dailyBudget: 'currency',
  budget: 'currency',
  start: 'date',
  end: 'date',
  // The target cell carries its own unit ("3.5× ROAS" / "$25 CPA"), so it
  // edits as text — stripping the $ would lose which target was meant.
  targetLabel: 'text',
};
